import { useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

// Tooltip — story 2-132. First consumer: the sidebar's collapsed (icon-only)
// nav rows, where a row's accessible name (aria-label, set by the caller on
// the wrapped trigger) has nowhere to render visually — this supplies the
// on-hover/on-focus VISUAL affordance for sighted mouse and keyboard users,
// distinct from (and never a replacement for) the trigger's own accessible
// name. The bubble is `aria-hidden` — the trigger's own aria-label is
// already the accessible name assistive tech announces; a second
// role="tooltip" element wired via aria-describedby would double-announce
// the same string for no benefit, so this stays a purely visual layer,
// shown on both mouse hover AND keyboard focus (`onFocusCapture`/
// `onBlurCapture` catch focus bubbling up from the wrapped trigger without
// needing to clone/modify it).
export interface TooltipProps {
  /** The visible bubble text — normally the same string as the trigger's own accessible name. */
  label: string;
  /** A single focusable/hoverable trigger (button, link, etc.) — rendered as-is, not cloned. */
  children: ReactNode;
  side?: 'right' | 'left' | 'top' | 'bottom';
  className?: string;
}

// Found live (story 2-141 review): the sidebar's collapsed-row tooltip
// silently disappeared, because the bubble used to be an absolutely-
// positioned child of the trigger's own wrapper — and the sidebar's root
// (`.shell-agent-glow`, story 2-132's ambient glow) carries `overflow:
// hidden` to clip that effect. Any child needing to render OUTSIDE its
// ancestor's box (which `left-full` does by design — the whole point of a
// side-anchored tooltip is escaping the trigger) gets clipped by that
// `overflow: hidden` regardless of z-index; z-index only orders siblings
// within a stacking context, it cannot override a clipped box. Same failure
// class this lane has hit repeatedly (TopBar's sticky positioning against an
// `overflow-hidden` ancestor), just clipping instead of un-stickying.
// Fixed the same way this repo's OWN existing floating-element primitive,
// `AnchoredPanel.tsx`, already escapes every ancestor — not a one-off
// workaround invented here. That file (already the popover engine behind
// every dropdown in the app) does the identical thing: `createPortal(...,
// document.body)`, a computed `style: CSSProperties` with `position:
// 'fixed'` derived from the anchor's own `getBoundingClientRect()`, and an
// escalating arbitrary `z-[N]` scale (`z-[70]`/`z-[80]` there, `z-90` on
// `Modal.tsx`) — this repo has no separate "z-index token" layer for
// portaled UI; the arbitrary z-[N] Tailwind scale IS that system, the same
// way `check-shell-geometry.mjs`'s own CLOSED file list (shell chrome only)
// deliberately does NOT cover this class of component. `z-[100]` continues
// that same scale one step above `Modal`'s 90, so a tooltip inside an open
// dialog's own content still renders above it. None of this position math
// could be a static class or CSS custom property either way — a tooltip's
// coordinates depend on WHERE its trigger happens to sit at that instant,
// which is runtime DOM measurement, not a token.
//
// Does NOT delegate to `AnchoredPanel` itself, despite sharing this exact
// positioning mechanism: the two primitives answer different behavioural
// questions, the same reason `Modal`'s `'bottom'`/`'right'` placements stay
// on ONE component while `Tabs`/`SegmentedControl` stay TWO (shared
// mechanism, divergent contract, is the split test either way runs). A
// tooltip is `aria-hidden`, decorative-only, shown on hover/focus of an
// ALREADY-focusable trigger, and never itself receives focus, an Esc
// handler, a backdrop or a focus trap — `AnchoredPanel`'s entire API is
// built around being a real, focusable `role="dialog"`/`"menu"` popover.
// Forcing Tooltip through that component would drag in Esc-to-close,
// backdrop-click and initial-focus behaviour a decorative label must never
// have.
// `position` is computed at show-time (not on every render) and held in
// state — the bubble stays ALWAYS mounted (opacity-toggled, same as before
// this fix), never conditionally rendered, so `getComputedStyle(...).opacity`
// still resolves to a real '0' before hover/focus, not "element not found".
// A component that unmounts on every hide is indistinguishable, to a test
// reading computed style, from one that was never fixed — the story-2-43
// spec's own "opacity idle=0" assertion depends on the element existing at
// rest.
export function Tooltip({ label, children, side = 'right', className }: TooltipProps): JSX.Element {
  const [visible, setVisible] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>({ position: 'fixed', left: -9999, top: -9999 });
  const triggerRef = useRef<HTMLSpanElement>(null);

  const measure = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const GAP = 8;
    const next: React.CSSProperties = { position: 'fixed' };
    if (side === 'right') {
      next.left = rect.right + GAP;
      next.top = rect.top + rect.height / 2;
      next.transform = 'translateY(-50%)';
    } else if (side === 'left') {
      next.right = window.innerWidth - rect.left + GAP;
      next.top = rect.top + rect.height / 2;
      next.transform = 'translateY(-50%)';
    } else if (side === 'top') {
      next.left = rect.left + rect.width / 2;
      next.bottom = window.innerHeight - rect.top + GAP;
      next.transform = 'translateX(-50%)';
    } else {
      next.left = rect.left + rect.width / 2;
      next.top = rect.bottom + GAP;
      next.transform = 'translateX(-50%)';
    }
    setStyle(next);
    setVisible(true);
  };

  return (
    <span
      ref={triggerRef}
      className={clsx('relative inline-flex', className)}
      onMouseEnter={measure}
      onMouseLeave={() => setVisible(false)}
      onFocusCapture={measure}
      onBlurCapture={() => setVisible(false)}
    >
      {children}
      {createPortal(
        <span
          role="presentation"
          aria-hidden="true"
          style={style}
          className={clsx(
            'pointer-events-none z-[100] whitespace-nowrap rounded-hairline border border-border-strong bg-popover px-2 py-1 text-xs font-medium text-popover-foreground shadow-elevated transition-opacity duration-150',
            visible ? 'opacity-100' : 'opacity-0',
          )}
        >
          {label}
        </span>,
        document.body,
      )}
    </span>
  );
}
